import { useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../context/AuthContext'

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']

function fmt(n) { return `$${parseFloat(n||0).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})}` }
function pct(a,b) { if(!b) return null; return (((a-b)/b)*100).toFixed(1) }
function Arrow({ val }) {
  if(val===null) return null
  const up = parseFloat(val)>0
  return <span style={{fontSize:11,fontWeight:700,color:up?'#10B981':'#EF4444',marginLeft:4}}>{up?'▲':'▼'}{Math.abs(val)}%</span>
}

export default function YearOverYearPage() {
  const { profile } = useAuth()
  const [invoices, setInvoices] = useState([])
  const [clients, setClients] = useState([])
  const [loading, setLoading] = useState(true)
  const [yearA, setYearA] = useState(new Date().getFullYear())
  const [yearB, setYearB] = useState(new Date().getFullYear()-1)

  useEffect(()=>{ load() },[])

  async function load() {
    const { data:c } = await supabase.from('clients').select('id,full_name,business_name,entity_type').eq('cpa_id',profile.id)
    setClients(c||[])
    const ids = (c||[]).map(x=>x.id)
    if(ids.length) {
      const { data:inv } = await supabase.from('invoices').select('*').in('client_id',ids).neq('status','draft').neq('status','cancelled')
      setInvoices(inv||[])
    }
    setLoading(false)
  }

  const clientName = id => { const c=clients.find(x=>x.id===id); return c?.business_name||c?.full_name||'Unknown' }

  function getYearData(year) {
    const yearInvs = invoices.filter(i=>new Date(i.created_at).getFullYear()===year)
    const paid = yearInvs.filter(i=>i.status==='paid')
    const total = yearInvs.reduce((s,i)=>s+(i.total_amount||0),0)
    const collected = paid.reduce((s,i)=>s+(i.total_amount||0),0)
    const outstanding = yearInvs.filter(i=>['sent','overdue'].includes(i.status)).reduce((s,i)=>s+(i.total_amount||0),0)
    const collRate = total>0?Math.round((collected/total)*100):0
    const monthly = MONTHS.map((_,m)=>({
      invoiced: yearInvs.filter(i=>new Date(i.created_at).getMonth()===m).reduce((s,i)=>s+(i.total_amount||0),0),
      collected: yearInvs.filter(i=>new Date(i.created_at).getMonth()===m&&i.status==='paid').reduce((s,i)=>s+(i.total_amount||0),0),
    }))
    const topClients = [...new Set(yearInvs.map(i=>i.client_id))].map(id=>({
      id, name:clientName(id),
      total:yearInvs.filter(i=>i.client_id===id).reduce((s,i)=>s+(i.total_amount||0),0),
      collected:yearInvs.filter(i=>i.client_id===id&&i.status==='paid').reduce((s,i)=>s+(i.total_amount||0),0),
      count:yearInvs.filter(i=>i.client_id===id).length
    })).sort((a,b)=>b.total-a.total).slice(0,8)
    return { total,collected,outstanding,collRate,monthly,topClients,count:yearInvs.length,paidCount:paid.length }
  }

  const dataA = getYearData(yearA)
  const dataB = getYearData(yearB)

  const maxMonthly = Math.max(...dataA.monthly.map(m=>Math.max(m.invoiced,m.collected)),...dataB.monthly.map(m=>Math.max(m.invoiced,m.collected)),1)

  const allYears = [...new Set(invoices.map(i=>new Date(i.created_at).getFullYear()))].sort((a,b)=>b-a)
  if(!allYears.includes(new Date().getFullYear())) allYears.unshift(new Date().getFullYear())
  if(!allYears.includes(new Date().getFullYear()-1)) allYears.push(new Date().getFullYear()-1)

  if(loading) return <div>{[...Array(4)].map((_,i)=><div key={i} className="skeleton" style={{height:80,marginBottom:12}}/>)}</div>

  return (
    <div>
      {/* Header */}
      <div className="card" style={{padding:'22px 26px',marginBottom:20,background:'#202326',border:'none'}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
          <div>
            <h2 style={{color:'#fff',fontSize:20,marginBottom:4}}>Year Over Year Comparison</h2>
            <p style={{color:'rgba(255,255,255,0.6)',fontSize:13,margin:0}}>Compare revenue, collections, and client activity across years.</p>
          </div>
          <div style={{display:'flex',gap:10,alignItems:'center'}}>
            <select value={yearA} onChange={e=>setYearA(parseInt(e.target.value))} style={{background:'rgba(255,255,255,0.1)',border:'1px solid rgba(255,255,255,0.2)',color:'#CBB17B',borderRadius:8,padding:'8px 14px',fontSize:13,fontWeight:700,cursor:'pointer'}}>
              {allYears.map(y=><option key={y} value={y} style={{background:'#202326'}}>{y}</option>)}
            </select>
            <span style={{color:'rgba(255,255,255,0.4)',fontSize:13}}>vs</span>
            <select value={yearB} onChange={e=>setYearB(parseInt(e.target.value))} style={{background:'rgba(255,255,255,0.1)',border:'1px solid rgba(255,255,255,0.2)',color:'rgba(255,255,255,0.7)',borderRadius:8,padding:'8px 14px',fontSize:13,fontWeight:700,cursor:'pointer'}}>
              {allYears.map(y=><option key={y} value={y} style={{background:'#202326'}}>{y}</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* Key metrics comparison */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:14,marginBottom:20}}>
        {[
          {label:'Total Invoiced',    a:dataA.total,    b:dataB.total,    fmt:true, icon:'🧾'},
          {label:'Total Collected',   a:dataA.collected,b:dataB.collected,fmt:true, icon:'💰'},
          {label:'Collection Rate',   a:dataA.collRate, b:dataB.collRate, fmt:false,icon:'📊', suffix:'%'},
          {label:'Invoice Count',     a:dataA.count,    b:dataB.count,    fmt:false,icon:'📋'},
        ].map(s=>(
          <div key={s.label} className="card" style={{padding:'18px 20px'}}>
            <div style={{fontSize:11,color:'#62615D',fontWeight:600,textTransform:'uppercase',letterSpacing:0.5,marginBottom:10}}>{s.icon} {s.label}</div>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-end'}}>
              <div>
                <div style={{fontSize:10,color:'#CBB17B',fontWeight:700,marginBottom:2}}>{yearA}</div>
                <div style={{fontSize:20,fontWeight:800,color:'#202326',fontFamily:"'DM Sans',sans-serif"}}>{s.fmt?fmt(s.a):s.a}{s.suffix||''}</div>
                <Arrow val={pct(s.a,s.b)} />
              </div>
              <div style={{textAlign:'right'}}>
                <div style={{fontSize:10,color:'#62615D',fontWeight:700,marginBottom:2}}>{yearB}</div>
                <div style={{fontSize:16,fontWeight:700,color:'#62615D'}}>{s.fmt?fmt(s.b):s.b}{s.suffix||''}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Monthly chart comparison */}
      <div className="card" style={{padding:'20px 22px',marginBottom:20}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16}}>
          <h3 style={{fontSize:14,fontWeight:700}}>Monthly Revenue Comparison</h3>
          <div style={{display:'flex',gap:16,fontSize:11}}>
            <span style={{display:'flex',alignItems:'center',gap:4}}><span style={{width:10,height:10,borderRadius:2,background:'#CBB17B',display:'inline-block'}}/>  {yearA}</span>
            <span style={{display:'flex',alignItems:'center',gap:4}}><span style={{width:10,height:10,borderRadius:2,background:'#93C5FD',display:'inline-block'}}/> {yearB}</span>
          </div>
        </div>
        <div style={{display:'flex',gap:4,alignItems:'flex-end',height:160}}>
          {MONTHS.map((m,i)=>{
            const aH = (dataA.monthly[i].invoiced/maxMonthly)*100
            const bH = (dataB.monthly[i].invoiced/maxMonthly)*100
            return (
              <div key={m} style={{flex:1,display:'flex',flexDirection:'column',alignItems:'center',height:'100%',justifyContent:'flex-end',gap:1}}>
                <div style={{width:'100%',display:'flex',gap:1,alignItems:'flex-end',height:136}}>
                  <div title={`${yearA} ${m}: ${fmt(dataA.monthly[i].invoiced)}`} style={{flex:1,background:'#CBB17B',borderRadius:'2px 2px 0 0',height:`${aH}%`,minHeight:dataA.monthly[i].invoiced>0?2:0,transition:'height 0.5s ease'}}/>
                  <div title={`${yearB} ${m}: ${fmt(dataB.monthly[i].invoiced)}`} style={{flex:1,background:'#93C5FD',borderRadius:'2px 2px 0 0',height:`${bH}%`,minHeight:dataB.monthly[i].invoiced>0?2:0,transition:'height 0.5s ease'}}/>
                </div>
                <div style={{fontSize:9,color:'#62615D',marginTop:2}}>{m}</div>
              </div>
            )
          })}
        </div>
      </div>

      {/* Top clients comparison */}
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:20}}>
        {[{year:yearA,data:dataA},{year:yearB,data:dataB}].map(({year,data})=>(
          <div key={year} className="card" style={{padding:0,overflow:'hidden'}}>
            <div style={{padding:'14px 18px',borderBottom:'1px solid var(--border)',background:'#F7F3EB',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
              <span style={{fontSize:14,fontWeight:700}}>Top Clients — {year}</span>
              <span style={{fontSize:12,color:'#62615D'}}>{fmt(data.total)} total</span>
            </div>
            {data.topClients.length===0 ? (
              <div style={{padding:24,textAlign:'center',color:'#62615D',fontSize:13}}>No data for {year}</div>
            ) : data.topClients.map((c,i)=>{
              const pctOfTotal = data.total>0?(c.total/data.total)*100:0
              const collPct = c.total>0?Math.round((c.collected/c.total)*100):0
              return (
                <div key={c.id} style={{padding:'12px 18px',display:'flex',alignItems:'center',gap:12,borderBottom:i<data.topClients.length-1?'1px solid var(--border)':'none'}}>
                  <div style={{width:24,height:24,borderRadius:'50%',background:'#202326',display:'flex',alignItems:'center',justifyContent:'center',color:'#CBB17B',fontSize:10,fontWeight:700,flexShrink:0}}>{i+1}</div>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:12,fontWeight:700,color:'#202326',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{c.name}</div>
                    <div style={{height:4,background:'#F0EBE0',borderRadius:2,marginTop:4,overflow:'hidden'}}>
                      <div style={{height:'100%',background:'#CBB17B',width:`${pctOfTotal}%`,borderRadius:2}}/>
                    </div>
                  </div>
                  <div style={{textAlign:'right',flexShrink:0}}>
                    <div style={{fontSize:13,fontWeight:800,color:'#202326'}}>{fmt(c.total)}</div>
                    <div style={{fontSize:10,color:collPct>=80?'#10B981':'#F59E0B'}}>{collPct}% collected</div>
                  </div>
                </div>
              )
            })}
          </div>
        ))}
      </div>
    </div>
  )
}
